import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import {
  sortAscIcon,
  sortDescIcon,
  sortIcon,
} from '../../icons'
import { getGroupRfidTags } from '../../modules/Rfid'
import { isAnyElementInArray } from '../../utils/arrayUtil'
import Loading from '../common/Loading'
import DetailRfidTagModal from './DetailRfidTagModal'
import './OverviewItemList.css'

type Props = {
  group: IGroup
  keyword?: string
}

type SortKey = 'name' | 'identifier' | 'payload'
type SortOrder = 'asc' | 'desc'

const normalizeValue = (value: string | null | undefined): string => (
  (value ?? '').trim().toLocaleLowerCase('ja')
)

const RfidTagsList = ({
  group,
  keyword = '',
}: Props) => {
  const [rfidTags, setRfidTags] = useState<IRfidTag[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedTag, setSelectedTag] = useState<IRfidTag | null>(null)
  const [sort, setSort] = useState<SortKey | null>(null)
  const [order, setOrder] = useState<SortOrder>('desc')
  const isMountedRef = useRef(true)
  const requestIdRef = useRef(0)

  useEffect(() => () => {
    isMountedRef.current = false
  }, [])

  const fetchRfidTags = useCallback(async () => {
    const requestId = requestIdRef.current + 1
    requestIdRef.current = requestId

    try {
      setLoading(true)
      const tags = await getGroupRfidTags(group.id)
      if (!isMountedRef.current || requestIdRef.current !== requestId) return
      setRfidTags(tags)
      setError(null)
    } catch (err) {
      if (!isMountedRef.current || requestIdRef.current !== requestId) return
      console.error('Failed to load RFID tags', err)
      setRfidTags([])
      setError('RFIDタグの読み込みに失敗しました')
    } finally {
      if (isMountedRef.current && requestIdRef.current === requestId) {
        setLoading(false)
      }
    }
  }, [group.id])

  useEffect(() => {
    fetchRfidTags()
  }, [fetchRfidTags])

  useEffect(() => {
    setSelectedTag(null)
  }, [group.id])

  const visibleTags = useMemo(() => {
    const normalizedKeyword = normalizeValue(keyword)
    const filtered = normalizedKeyword.length === 0
      ? [...rfidTags]
      : rfidTags.filter((tag) => (
        [tag.name, tag.identifier, tag.payload]
          .some((value) => normalizeValue(value).includes(normalizedKeyword))
      ))

    if (!sort) return filtered

    filtered.sort((left, right) => (
      normalizeValue(left[sort]).localeCompare(
        normalizeValue(right[sort]),
        'ja',
        { numeric: true, sensitivity: 'base' },
      )
    ))
    if (order === 'desc') filtered.reverse()
    return filtered
  }, [keyword, order, rfidTags, sort])

  const onClickSortHeader = (key: SortKey) => {
    if (sort !== key) {
      setSort(key)
      setOrder('asc')
      return
    }
    setOrder((previous) => (previous === 'asc' ? 'desc' : 'asc'))
  }

  const getSortHeaderIcon = (key: SortKey) => {
    if (sort !== key) return sortIcon
    return order === 'asc' ? sortAscIcon : sortDescIcon
  }

  const emptyRowText = useMemo(() => {
    if (loading) return 'RFIDタグを読み込み中...'
    if (error) return error
    if (keyword.trim()) return '検索条件に一致するRFIDタグがありません。'
    return 'このグループにはRFIDタグがありません。'
  }, [error, keyword, loading])

  return (
    <div className="overview-list rfid-tags-list" data-testid="rfid-tags-list">
      <Loading show={loading} />
      <table className="table">
        <thead className="header">
          <tr>
            {([
              ['name', 'タグ名'],
              ['identifier', 'RFID識別子'],
              ['payload', 'Payload'],
            ] as [SortKey, string][]).map(([key, label]) => (
              <th key={key}>
                <button
                  type="button"
                  className="title rfid-tags-list__sort"
                  onClick={() => onClickSortHeader(key)}
                  data-testid={`rfid-tags-header-sort-${key}`}
                >
                  {label}
                  <img alt="sort" src={getSortHeaderIcon(key)} />
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="list">
          {!isAnyElementInArray(visibleTags) ? (
            <tr>
              <td colSpan={3} className="list-row">{emptyRowText}</td>
            </tr>
          ) : visibleTags.map((tag) => (
            <React.Fragment key={tag.id}>
              <tr
                className="item list-row"
                onClick={() => setSelectedTag(tag)}
                data-testid={`rfid-tag-row-${tag.id}`}
              >
                <td>{tag.name}</td>
                <td className="rfid-code">{tag.identifier}</td>
                <td>{tag.payload || '-'}</td>
              </tr>
              <tr className="divider">
                <td colSpan={3}><div className="line" /></td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
      {selectedTag && (
        <DetailRfidTagModal
          modalProps={{
            rfidTag: selectedTag,
            group,
            refreshList: fetchRfidTags,
          }}
          closeModalFunction={() => setSelectedTag(null)}
        />
      )}
    </div>
  )
}

export default RfidTagsList
